import { inject } from '@angular/core';
import { Router, CanActivateFn, CanActivateChildFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { GlobalService } from '../services/global.service';
import { environment } from 'src/environments/environment';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const globalService = inject(GlobalService);

  // DEV MODE — skip auth em desenvolvimento
  if ((environment as any).features?.skipAuthInDev) {
    return true;
  }

  // 🌐 Rotas públicas — acessíveis sem autenticação (homepage, seguros e guias/blog)
  // Inclui URLs com query params (ex: /?fbclid=... vindo da Meta/Facebook/LinkedIn)
  const isPublicRoute =
    state.url === '/' ||
    state.url.startsWith('/?') ||
    state.url === '/home' ||
    state.url.startsWith('/home?') ||
    state.url.startsWith('/seguros') ||
    state.url.startsWith('/participar-sinistro') ||
    state.url.startsWith('/guias') ||
    state.url.startsWith('/blog');
  if (isPublicRoute && !authService.isLoggedin(globalService.loggedUserClass)) {
    // Tentar restaurar sessão silenciosamente (para mostrar UI autenticada se possível)
    const restoredUser = authService.restoreSession();
    if (restoredUser) {
      globalService.loggedUserClass = restoredUser;
    }
    // Mesmo sem sessão, permitir acesso à homepage e seguros
    return true;
  }

  // Verificar autenticação (normal)
  if (!authService.isLoggedin(globalService.loggedUserClass)) {
    // ⚠️ FIX: Tentar restaurar sessão (F5/Refresh Race Condition)
    const restoredUser = authService.restoreSession();

    if (restoredUser) {
      globalService.loggedUserClass = restoredUser;
    } else {
      authService.logout(false, '', false);
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }
  }

  // ✅ Log de navegação movido para NavigationLoggerService (escuta NavigationEnd)
  // para não atrasar a transição de rota com HTTP requests síncronos.

  // 🔏 MERGE DATA FROM PARENTS (Sem herdar All: 1 genérico do wrapper PagesComponent)
  const leafRoute = route.firstChild ? route.firstChild : route;
  const isProtectedAdminOrAreaUrl =
    /^\/(vault(\/|$|\?)|permissions(\/|$|\?)|sitemanagement(\/|$|\?)|commercialarea(\/|$|\?)|intermediaryarea(\/|$|\?)|clientarea(\/|$|\?))/i.test(
      state.url,
    );

  let mergedData: Record<string, any> = { ...leafRoute.data };
  let p = leafRoute.parent;
  while (p) {
    const { All: _ignoredParentAll, ...parentDataWithoutAll } = p.data || {};
    mergedData = { ...parentDataWithoutAll, ...mergedData };
    p = p.parent;
  }

  // Só considera All === 1 se a rota folha (ou a rota ativada diretamente) definir explicitamente All: 1
  const explicitLeafAll =
    (leafRoute.data?.['All'] === 1 || leafRoute.data?.['All'] === '1') && !isProtectedAdminOrAreaUrl;
  if (!explicitLeafAll) {
    mergedData['All'] = 0;
  }

  return checkPermission(mergedData, state.url, globalService, authService, router);
};

/**
 * ✅ OPTIMIZADO: O authChildGuard agora verifica autenticação e permissões de rota filha,
 * sem repetir SetSiteRoutesLog nem logs pesados.
 */
export const authChildGuard: CanActivateChildFn = (_route, _state) => {
  const authService = inject(AuthService);
  const globalService = inject(GlobalService);
  const router = inject(Router);

  // DEV MODE — skip auth em desenvolvimento
  if ((environment as any).features?.skipAuthInDev) {
    return true;
  }

  // 🌐 Rotas públicas — acessíveis sem autenticação (homepage, seguros e guias/blog)
  // Inclui URLs com query params (ex: /?fbclid=... vindo da Meta/Facebook/LinkedIn)
  const isPublicRoute =
    _state.url === '/' ||
    _state.url.startsWith('/?') ||
    _state.url === '/home' ||
    _state.url.startsWith('/home?') ||
    _state.url.startsWith('/seguros') ||
    _state.url.startsWith('/participar-sinistro') ||
    _state.url.startsWith('/guias') ||
    _state.url.startsWith('/blog');

  // Verificação rápida de autenticação (sem HTTP requests nem logs)
  if (!authService.isLoggedin(globalService.loggedUserClass)) {
    // Tentar restaurar sessão silenciosamente
    const restoredUser = authService.restoreSession();
    if (restoredUser) {
      globalService.loggedUserClass = restoredUser;
    } else if (isPublicRoute) {
      // Se é rota pública, permitir acesso mesmo sem sessão
      return true;
    } else {
      // Sem sessão e rota protegida — redirecionar para login
      authService.logout(false, '', false);
      router.navigate(['/login'], { queryParams: { returnUrl: _state.url } });
      return false;
    }
  }

  if (isPublicRoute) {
    return true;
  }

  // Se a rota filha define explicitamente permissões ou All: 0, validar permissões
  const childData = _route.data || {};
  const hasExplicitRoleCheck =
    childData['All'] === 0 ||
    childData['Admin'] === 1 ||
    childData['SiteManagement'] === 1 ||
    childData['CommercialArea'] === 1 ||
    childData['IntermediaryArea'] === 1 ||
    childData['ClientArea'] === 1;

  if (hasExplicitRoleCheck) {
    return checkPermission(childData, _state.url, globalService, authService, router);
  }

  return true;
};

function checkPermission(
  data: any,
  url: string,
  globalService: GlobalService,
  authService: AuthService,
  router: Router,
): boolean {
  const user = globalService.loggedUserClass;

  if (data['All'] === 1 || data['All'] === '1' || data['All'] === true) {
    return true;
  }

  if (user?.Admin) {
    return true;
  }

  if (data['ClientArea'] === 1 && (user?.ClientArea || user?.SiteManagement)) return true;
  if (
    data['CommercialArea'] === 1 &&
    (user?.CommercialArea || user?.idCommercial || user?.commercialClass?.idCommercial)
  )
    return true;
  if (data['IntermediaryArea'] === 1 && user?.IntermediaryArea) return true;
  if (data['SiteManagement'] === 1 && user?.SiteManagement) return true;

  // 🛡️ SECURITY: Instead of logout, redirect to their area to avoid "dead ends"
  // but prevent Infinite loops by ensuring we don't redirect back to the forbidden route
  const fallback = user?.NavigateTo || '/clientarea';

  // Only logout if we are already at the fallback or if we have no user session at all
  if (!user || fallback === url) {
    authService.logout(false, '', false);
    router.navigate(['/login']);
  } else {
    router.navigate([fallback]);
  }

  return false;
}
